/**
 * Workbook 文章管理模块
 * 自动扫描 articles 目录下的 markdown 文件
 */

export interface WorkbookArticle {
  id: string;
  title: string;
  date: string;
  tags: string[];
  file: string;
  content: string;
}

export interface ArticleFrontmatter {
  title: string;
  date: string;
  tags?: string[];
}

/**
 * 解析 frontmatter
 */
function parseFrontmatter(content: string): { frontmatter: ArticleFrontmatter; body: string } {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = content.match(frontmatterRegex);

  if (!match) {
    return {
      frontmatter: { title: 'Untitled', date: new Date().toISOString().split('T')[0], tags: [] },
      body: content
    };
  }

  const frontmatterText = match[1];
  const body = match[2];

  // 简单解析 YAML
  const frontmatter: ArticleFrontmatter = {
    title: 'Untitled',
    date: new Date().toISOString().split('T')[0],
    tags: []
  };

  frontmatterText.split('\n').forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.slice(0, colonIndex).trim();
      let value = line.slice(colonIndex + 1).trim();

      if (key === 'tags') {
        // 解析数组格式 [tag1, tag2] 或 - tag1\n- tag2
        if (value.startsWith('[') && value.endsWith(']')) {
          frontmatter.tags = value
            .slice(1, -1)
            .split(',')
            .map(t => t.trim().replace(/["']/g, ''))
            .filter(t => t);
        }
      } else {
        (frontmatter as any)[key] = value.replace(/["']/g, '');
      }
    }
  });

  return { frontmatter, body };
}

/**
 * 从文件路径生成 ID
 */
function generateId(filePath: string): string {
  const filename = filePath.split('/').pop() || filePath.split('\\').pop() || '';
  return filename.replace(/\.md$/, '');
}

/**
 * 加载所有文章
 * 使用 Vite 的 import.meta.glob 自动扫描
 */
export async function loadAllArticles(): Promise<WorkbookArticle[]> {
  // Vite 自动扫描所有 markdown 文件
  const articleFiles = import.meta.glob('./articles/**/*.md', { as: 'raw' });

  const articles: WorkbookArticle[] = [];

  for (const [path, loader] of Object.entries(articleFiles)) {
    try {
      const content = await loader() as string;
      const { frontmatter, body } = parseFrontmatter(content);

      articles.push({
        id: generateId(path),
        title: frontmatter.title,
        date: frontmatter.date,
        tags: frontmatter.tags || [],
        file: path,
        content: body
      });
    } catch (error) {
      console.error(`Failed to load article: ${path}`, error);
    }
  }

  // 按日期降序排序（最新的在前）
  return articles.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

/**
 * 根据 ID 加载单篇文章
 */
export async function loadArticleById(id: string): Promise<WorkbookArticle | null> {
  const articles = await loadAllArticles();
  return articles.find(a => a.id === id) || null;
}

/**
 * 按标签筛选文章
 */
export async function loadArticlesByTag(tag: string): Promise<WorkbookArticle[]> {
  const articles = await loadAllArticles();
  return articles.filter(a => a.tags.includes(tag));
}

/**
 * 获取所有标签
 */
export async function getAllTags(): Promise<string[]> {
  const articles = await loadAllArticles();
  const tagSet = new Set<string>();
  articles.forEach(a => a.tags.forEach(t => tagSet.add(t)));
  return Array.from(tagSet).sort();
}
